import {
  computed,
  DestroyRef,
  effect,
  inject,
  Service,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { AnyResource, File } from '@models';

import {
  AiConversation,
  AiMode,
} from '@shared/components/ai-assistant/ai-assistant.types';
import {
  AI_MODAL_CLOSED,
  AiPanel,
  expandedCapFor,
} from '@shared/components/ai-assistant/ai-dock.layout';
import { AppState } from '@store';

import { ModalService } from './modal.service';

export interface AiPanelRequest {
  resource: AnyResource;
  mode: AiMode;
  conversation?: AiConversation;
  title?: string;
}

const scopeOf = (resource: AnyResource, conversation?: AiConversation) =>
  conversation?.resourceRids ?? [resource.rid];

const isSameScope = (left: number[], right: number[]) =>
  left.length === right.length && left.every((rid) => right.includes(rid));

@Service()
export class AiDockService {
  modalService = inject(ModalService);
  destroyRef = inject(DestroyRef);
  store = inject<Store<AppState>>(Store);

  isLoggedIn = toSignal(
    this.store.select((state) => state.auth.isLoggedIn),
    { requireSync: true }
  );
  userId = toSignal(
    this.store.select((state) => state.user.userId ?? null),
    { requireSync: true }
  );

  panels = signal<AiPanel[]>([]);
  ownerId = signal<Nullable<number>>(this.userId());
  visiblePanels = computed(() => {
    const userId = this.userId();

    return this.isLoggedIn() && userId !== null && userId === this.ownerId()
      ? this.panels()
      : [];
  });
  viewportWidth = signal(
    typeof window !== 'undefined' ? window.innerWidth : 992
  );

  expandedCap = computed(() => expandedCapFor(this.viewportWidth()));

  seq = 0;

  constructor() {
    if (typeof window !== 'undefined') {
      const onResize = () => this.viewportWidth.set(window.innerWidth);

      window.addEventListener('resize', onResize);
      this.destroyRef.onDestroy(() =>
        window.removeEventListener('resize', onResize)
      );
    }

    effect(() => {
      this.expandedCap();
      untracked(() => this.enforceCap());
    });

    effect(() => {
      const isLoggedIn = this.isLoggedIn();
      const userId = this.userId();

      untracked(() => {
        if (!isLoggedIn) {
          this.panels.set([]);
        } else if (userId !== null && userId !== this.ownerId()) {
          this.panels.set([]);
          this.ownerId.set(userId);
        }
      });
    });
  }

  open = ({ resource, mode, conversation, title }: AiPanelRequest): string => {
    const scope = scopeOf(resource, conversation);
    const existing = this.panels().find(
      (panel) =>
        panel.mode === mode &&
        isSameScope(scopeOf(panel.resource, panel.conversation), scope)
    );

    if (existing) {
      this.restore(existing.id);

      return existing.id;
    }

    const panel: AiPanel = {
      id: crypto.randomUUID(),
      resource,
      mode,
      title: title ?? (resource.displayName || resource.name || ''),
      state: 'expanded',
      seq: ++this.seq,
      conversation: conversation ?? {
        mode,
        messages: [],
        resourceRids: [resource.rid],
      },
    };

    this.panels.update((list) => [...list, panel]);
    this.enforceCap();

    return panel.id;
  };

  restore = (id: string) => {
    this.panels.update((list) =>
      list.map((panel) =>
        panel.id === id
          ? { ...panel, state: 'expanded', seq: ++this.seq }
          : panel
      )
    );
    this.enforceCap();
  };

  minimize = (id: string) => {
    this.panels.update((list) =>
      list.map((panel) =>
        panel.id === id ? { ...panel, state: 'minimized' } : panel
      )
    );
  };

  close = (id: string) => {
    this.panels.update((list) => list.filter((panel) => panel.id !== id));
  };

  enforceCap = () => {
    const cap = this.expandedCap();
    const expanded = this.panels()
      .filter((panel) => panel.state === 'expanded')
      .sort((a, b) => a.seq - b.seq);
    const excess = expanded.length - cap;

    if (excess <= 0) {
      return;
    }

    const demote = new Set(expanded.slice(0, excess).map((panel) => panel.id));

    this.panels.update((list) =>
      list.map((panel) =>
        demote.has(panel.id) ? { ...panel, state: 'minimized' } : panel
      )
    );
  };

  expandToModal = (id: string, conversation: AiConversation) => {
    const panel = this.panels().find((item) => item.id === id);

    if (!panel) {
      return Promise.resolve();
    }

    this.close(id);

    return this.showInModal({
      resource: panel.resource,
      conversation,
      title: panel.title,
    });
  };

  showSelectionInModal = ({
    files,
    mode,
    container,
  }: {
    files: File[];
    mode: AiMode;
    container?: AnyResource;
  }) => {
    const [first] = files;

    if (!first) {
      return Promise.resolve();
    }

    const firstName = first.displayName || first.name || '';

    return this.showInModal({
      resource: container ?? first,
      conversation: {
        mode,
        messages: [],
        resourceRids: files.map((file) => file.rid),
      },
      title: files.length > 1 ? `${firstName} +${files.length - 1}` : firstName,
    });
  };

  showInModal = async ({
    resource,
    conversation,
    title,
  }: Omit<AiPanelRequest, 'mode'> & { conversation: AiConversation }) => {
    const { AiAssistantModalComponent } =
      await import('@shared/components/ai-assistant/ai-assistant-modal.component');

    const redock = (next: unknown) => {
      if (!this.isLoggedIn()) {
        return;
      }

      const convo =
        next && typeof next === 'object' && 'mode' in next
          ? (next as AiConversation)
          : conversation;

      this.open({ resource, mode: convo.mode, conversation: convo, title });
    };

    this.modalService
      .open(
        AiAssistantModalComponent,
        { resource, conversation },
        { modalDialogClass: 'ai-modal' }
      )
      .then(
        (result) => {
          if (result !== AI_MODAL_CLOSED) {
            redock(result);
          }
        },
        (dismissed) => redock(dismissed)
      );
  };
}
